import * as React from 'react';
import { PageCard, PageHeader, PageMenu } from '@relay-towing/ui';
import { useNavigate } from 'react-router-dom';
import { useMember } from '../../hooks/useMember';
import {
  functionsFor,
  mobileTabsFor,
  type FunctionId,
} from '../../lib/driverAppTabs';
import { rememberFunction } from '../../lib/lastFunction';
import { TabScreen } from '../shell/TabScreen';

/**
 * One Functions page: Impounds, Invoices, Inspections or
 * Checklists. For a member with
 * the Functions tab it is a card whose title opens the other pages
 * (Figma 3-50, 3-543), and is remembered, so the tab opens on it next time.
 * Someone who only drives has each page as a tab of its own, so the page
 * is a plain one under its title.
 */
export function FunctionPage({
  id,
  title,
  actions,
  children,
}: {
  id: FunctionId;
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  const member = useMember();
  const navigate = useNavigate();
  const framed = mobileTabsFor(member).includes('functions');

  React.useEffect(() => {
    if (framed) rememberFunction(id);
  }, [framed, id]);

  if (!framed)
    return (
      <TabScreen>
        <PageHeader title={title} layout="row" actions={actions} />
        {children}
      </TabScreen>
    );

  const pages = functionsFor(member);
  return (
    <TabScreen>
      <PageCard
        title={
          <PageMenu
            value={id}
            options={pages.map(({ id, label, icon }) => ({
              value: id,
              label,
              icon,
            }))}
            onValueChange={(next) => {
              const page = pages.find((item) => item.id === next);
              // Moving between pages is not a step to go back through.
              if (page && 'screen' in page.opens)
                navigate(page.opens.screen, { replace: true });
            }}
          />
        }
        actions={actions}
      >
        {children}
      </PageCard>
    </TabScreen>
  );
}
